package uk.co.nickthecoder.kyd.appearance

import uk.co.nickthecoder.glok.application.Platform
import uk.co.nickthecoder.glok.backend.Texture
import uk.co.nickthecoder.glok.backend.backend
import uk.co.nickthecoder.glok.property.boilerplate.booleanProperty
import uk.co.nickthecoder.glok.property.invalidationListener
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.util.Matrix
import uk.co.nickthecoder.kyd.Actor
import uk.co.nickthecoder.kyd.ParentResource
import uk.co.nickthecoder.kyd.ResourceType
import uk.co.nickthecoder.kyd.util.Vector2
import uk.co.nickthecoder.kyd.util.copyTexture

/**
 * A small rectangular region of a [Graphic] that can be used as an [Actor]'s [Appearance].
 *
 * For example, suppose our game contains an animal that can walk left and right.
 * We will have a [Pose] for each frame of the animation as it walks.
 * The [name]s of these Poses might be `left0`, `left1`, `left2`, `right0` etc.
 * We might also have poses for when it isn't moving called `idle0`, `idle1` etc.
 *
 * Rather than creating a .png for each of these, we create a single [Graphic] and declare rectangular
 * regions within it for each [Pose].
 */
open class Pose(

    parent: ParentResource,
    name: String,
    graphicPath: String

) : PartialGraphic(parent, name, graphicPath), AppearanceResource {

    override val resourceType: ResourceType
        get() = ResourceType.POSE

    internal val repeatingProperty by booleanProperty(false)
    var repeating: Boolean by repeatingProperty

    /**
     * A _copy_ of the part of the [Graphic] containing just this pose's bounds, which can be used as
     * a repeating pattern in X & Y.
     */
    private var repeatingTexture: Texture? = null

    private val resizeListener = invalidationListener {
        // TODO repeatingTexture?.destroy()
        repeatingTexture = null
    }

    init {
        for (prop in listOf(
            graphicLeftProperty, graphicTopProperty, graphicWidthProperty, graphicHeightProperty,
            repeatingProperty
        )) {
            prop.addListener(resizeListener)
        }
    }

    /**
     * If a pose is to be used as a repeating pattern, we need to copy it into a new Texture.
     * The pose's dimensions must be powers of 2.
     *
     */
    fun repeatingTexture(): Texture? {

        if (repeating) {
            repeatingTexture?.let { return it }

            // TODO Glok bug, GLTextures were reported as not loaded, even though they are!
            // Remove the extra test when using the next version of Glok.
            if ((! graphic.texture.toString().contains("GLTexture")) && ! graphic.texture.isLoaded) {
                println("Not loaded. cannot create repeating texture.")
                return null
            }
            // We shouldn't try to draw to texture while the scene is being drawn, so use runLater.
            Platform.runLater {
                repeatingTexture = backend.createTexture("$name-repeating", graphicWidth, graphicHeight).apply {
                    setRepeating(x = true, y = true)
                    copyTexture(
                        graphic.texture,
                        graphicLeft, graphicTop, graphicWidth, graphicHeight, this
                    )
                }
            }
            return repeatingTexture
        }
        return null
    }

    override fun appearanceForActor(actor: Actor): Appearance {
        val result = if (repeating) {
            RepeatingPose(this).apply {
                val oldBounds = actor.appearance.localBounds()
                // For a "new" actor (in the editor), we use the "default" size,
                // but when the actor already HAS bounds, then we use its previous bound.
                if (oldBounds !== NoAppearance.NO_BOUNDS) {
                    corner0 = oldBounds.first
                    corner2 = oldBounds.second
                }
            }
        } else {
            PoseAppearance(this)
        }
        result.tint = actor.appearance.tintIfAvailable
        return result
    }

    override fun appearance(): Appearance {
        return if (repeating) {
            RepeatingPose(this)
        } else {
            PoseAppearance(this)
        }
    }

    override fun toString() = "Pose $name $graphicWidth x $graphicHeight pin @ $pin"
}
